import { useState, useCallback, useRef, useMemo, type RefObject } from "react";
import { clampPan } from "./clampPan";
import { NODE_WIDTH } from "../drawNode/drawNode";

interface Position {
  x: number;
  y: number;
}

interface ContentBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

interface UseCanvasInteractionsOptions {
  canvasSize?: { width: number; height: number };
  contentBounds?: ContentBounds | null;
  // Used to convert touch coordinates to a canvas-relative offset for anchoring pinch-zoom.
  canvasRef?: RefObject<HTMLCanvasElement | null>;
  // Called when a two-finger pinch gesture ends. Given the current pan/zoom, return a new
  // pan to snap to (e.g. centering the nearest node when the pinch left the view centered
  // on empty space), or null to leave the pan as-is.
  getSnapPan?: (pan: Position, zoom: number) => Position | null;
}

// Keep at least one full card width visible on screen at any pan extreme.
const PAN_MARGIN = NODE_WIDTH;

const DEFAULT_CANVAS_SIZE = { width: 0, height: 0 };

export const useCanvasInteractions = (options?: UseCanvasInteractionsOptions) => {
  const canvasSize = options?.canvasSize ?? DEFAULT_CANVAS_SIZE;
  const contentBounds = options?.contentBounds ?? null;
  const canvasRef = options?.canvasRef;
  const getSnapPan = options?.getSnapPan;

  const [isDragging, setIsDragging] = useState(false);
  const [pan, setPanState] = useState<Position>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [lastTouchDistance, setLastTouchDistance] = useState<number | null>(null);
  const [isPinching, setIsPinching] = useState(false);
  const [mouseDownOnCanvas, setMouseDownOnCanvas] = useState(false);
  const dragStartRef = useRef<Position>({ x: 0, y: 0 });
  const panStartRef = useRef<Position>({ x: 0, y: 0 });
  const pendingZoomRef = useRef<number>(1);
  const lastTouchMoveTimeRef = useRef<number>(0);

  const isMac = typeof navigator !== "undefined" && navigator.userAgent.toLowerCase().includes("mac");

  const isMobile = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }, []);

  const touchConfig = useMemo(
    () => ({
      dragSensitivity: isMobile ? 0.75 : 1.0,
      pinchZoomSensitivity: isMobile ? 0.85 : 1.0,
      minMoveThreshold: isMobile ? 2 : 0,
      throttleMs: isMobile ? 16 : 0,
    }),
    [isMobile],
  );

  const getTouchDistance = (touches: React.TouchList) => {
    if (touches.length < 2) return null;
    const touch1 = touches[0];
    const touch2 = touches[1];
    return Math.sqrt(Math.pow(touch2.clientX - touch1.clientX, 2) + Math.pow(touch2.clientY - touch1.clientY, 2));
  };

  // Midpoint between two touches, relative to the canvas center — same coordinate space
  // as the cursorOffset passed to handleWheelZoom, so pinch-zoom can anchor to it the
  // same way wheel-zoom anchors to the mouse cursor.
  const getTouchMidpointOffset = useCallback(
    (touches: React.TouchList): Position | null => {
      if (touches.length < 2 || !canvasRef?.current) return null;
      const rect = canvasRef.current.getBoundingClientRect();
      const midX = (touches[0].clientX + touches[1].clientX) / 2;
      const midY = (touches[0].clientY + touches[1].clientY) / 2;
      return {
        x: midX - rect.left - canvasSize.width / 2,
        y: midY - rect.top - canvasSize.height / 2,
      };
    },
    [canvasRef, canvasSize.width, canvasSize.height],
  );

  // Immediate zoom update for smooth, live zooming
  const updateZoom = useCallback((newZoom: number) => {
    const clampedZoom = Math.max(0.01, Math.min(3, newZoom));
    pendingZoomRef.current = clampedZoom;
    setZoom(clampedZoom);
  }, []);

  // Clamps pan so the tree content can't be dragged fully off-screen. Uses the latest
  // pending zoom (rather than the possibly-stale `zoom` state) so it stays correct even
  // when a pan and zoom update happen in the same gesture (e.g. wheel-zoom).
  const setPan = useCallback(
    (value: Position | ((prev: Position) => Position)) => {
      setPanState((prev) => {
        const next = typeof value === "function" ? value(prev) : value;
        return clampPan(next, {
          contentBounds,
          canvasSize,
          zoom: pendingZoomRef.current,
          margin: PAN_MARGIN,
        });
      });
    },
    [contentBounds, canvasSize],
  );

  // Applies a new zoom level while keeping the world point at `anchorOffset` (relative to
  // the view center) fixed on screen. Shared by wheel-zoom (cursor-anchored) and pinch-zoom
  // (touch-midpoint-anchored). Routing through setPan also re-runs pan clamping on every
  // zoom step, so the content can't drift out of the viewport mid-gesture.
  const zoomAroundPoint = useCallback(
    (newZoom: number, anchorOffset: Position) => {
      const oldZoom = pendingZoomRef.current;
      const clampedZoom = Math.max(0.01, Math.min(3, newZoom));
      if (clampedZoom === oldZoom) return;

      const invDelta = 1 / oldZoom - 1 / clampedZoom;
      setPan((prev) => ({
        x: prev.x - anchorOffset.x * invDelta,
        y: prev.y - anchorOffset.y * invDelta,
      }));
      updateZoom(clampedZoom);
    },
    [updateZoom, setPan],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button === 0) {
        setMouseDownOnCanvas(true);
        setIsDragging(true);
        dragStartRef.current = { x: e.clientX, y: e.clientY };
        panStartRef.current = { x: pan.x, y: pan.y };
      }
    },
    [pan],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !mouseDownOnCanvas) return;

      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;

      const zoomAdjustedSensitivity = 1.0 / zoom;

      setPan({
        x: panStartRef.current.x + deltaX * zoomAdjustedSensitivity,
        y: panStartRef.current.y + deltaY * zoomAdjustedSensitivity,
      });
    },
    [isDragging, mouseDownOnCanvas, zoom, setPan],
  );

  const handleMouseUp = useCallback(() => {
    if (mouseDownOnCanvas && isDragging) {
      setIsDragging(false);
      setMouseDownOnCanvas(false);
    }
  }, [isDragging, mouseDownOnCanvas]);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2) {
        setLastTouchDistance(getTouchDistance(e.touches));
        setIsPinching(true);
      } else if (e.touches.length === 1) {
        setIsDragging(true);
        dragStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        panStartRef.current = { x: pan.x, y: pan.y };
        lastTouchMoveTimeRef.current = 0;
      }
    },
    [pan],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const now = Date.now();
      if (touchConfig.throttleMs > 0 && now - lastTouchMoveTimeRef.current < touchConfig.throttleMs) {
        return;
      }
      lastTouchMoveTimeRef.current = now;

      if (e.touches.length === 2 && lastTouchDistance) {
        const currentDistance = getTouchDistance(e.touches);
        if (currentDistance) {
          const zoomDelta = (currentDistance / lastTouchDistance - 1) * touchConfig.pinchZoomSensitivity;
          const zoomFactor = 1 + zoomDelta;
          const anchorOffset = getTouchMidpointOffset(e.touches);
          if (anchorOffset) {
            zoomAroundPoint(pendingZoomRef.current * zoomFactor, anchorOffset);
          } else {
            updateZoom(pendingZoomRef.current * zoomFactor);
          }
          setLastTouchDistance(currentDistance);
        }
      } else if (isDragging && e.touches.length === 1) {
        e.preventDefault();

        const deltaX = e.touches[0].clientX - dragStartRef.current.x;
        const deltaY = e.touches[0].clientY - dragStartRef.current.y;

        const totalDelta = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        if (totalDelta < touchConfig.minMoveThreshold) {
          return;
        }

        const baseSensitivity = isMac ? 0.7 : touchConfig.dragSensitivity;
        const zoomAdjustedSensitivity = baseSensitivity / zoom;

        setPan({
          x: panStartRef.current.x + deltaX * zoomAdjustedSensitivity,
          y: panStartRef.current.y + deltaY * zoomAdjustedSensitivity,
        });
      }
    },
    [
      isDragging,
      lastTouchDistance,
      isMac,
      touchConfig,
      updateZoom,
      zoom,
      setPan,
      getTouchMidpointOffset,
      zoomAroundPoint,
    ],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length < 2) {
        setLastTouchDistance(null);
        if (isPinching) {
          setIsPinching(false);
          const snapPan = getSnapPan?.(pan, pendingZoomRef.current);
          if (snapPan) setPan(snapPan);
        }
      }
      setIsDragging(false);
      lastTouchMoveTimeRef.current = 0;
    },
    [isPinching, getSnapPan, pan, setPan],
  );

  const resetPan = useCallback(() => {
    setPan({ x: 0, y: 0 });
  }, [setPan]);

  const resetZoom = useCallback(() => {
    updateZoom(1);
  }, [updateZoom]);

  const handleWheelZoom = useCallback(
    (deltaY: number, cursorOffset: Position) => {
      const zoomFactor = deltaY < 0 ? 1.1 : 1 / 1.1;
      zoomAroundPoint(pendingZoomRef.current * zoomFactor, cursorOffset);
    },
    [zoomAroundPoint],
  );

  return {
    pan,
    setPan,
    zoom,
    setZoom: updateZoom,
    isDragging,
    mouseDownOnCanvas,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    resetPan,
    resetZoom,
    handleWheelZoom,
  };
};
